import api from './api';
import type {
  PurchaseOrder,
  PurchaseOrderLine,
  GoodsReceipt,
  PaginatedResponse,
  PurchaseOrderStatus,
} from '../types/venue';

/**
 * Build the PO base path. When `venueId` is provided we use the venue-scoped
 * mount (`/admin/venue/:venueId/purchase-order`) — required for VENUE_USER and
 * also fine for ADMIN/SYSTEM_ADMIN. When omitted we fall back to the global
 * mount (`/admin/purchase-order`), which is admin-only on the backend; that
 * branch exists for the cross-venue supplier history view in SupplierDetail.
 */
const buildBase = (venueId?: string) =>
  venueId ? `/admin/venue/${venueId}/purchase-order` : '/admin/purchase-order';

const buildGrBase = (venueId?: string) =>
  venueId ? `/admin/venue/${venueId}/goods-receipt` : '/admin/goods-receipt';

const purchaseOrderService = {
  getOrders: async (
    venueId: string | undefined,
    page = 1,
    pageSize = 20,
    status?: PurchaseOrderStatus,
    supplierId?: string,
  ): Promise<PaginatedResponse<PurchaseOrder>> => {
    // On the venue-scoped mount the URL carries venueId; on the global mount
    // (admin-only) we drop it entirely — we don't trust client-supplied venueId
    // for authorization, and SupplierDetail only filters by supplierId there.
    const params: Record<string, unknown> = { page, pageSize };
    if (status) params.status = status;
    if (supplierId) params.supplierId = supplierId;
    const response = await api.get(buildBase(venueId), { params });
    return response.data;
  },

  getOrder: async (venueId: string | undefined, poId: string): Promise<PurchaseOrder> => {
    const response = await api.get(`${buildBase(venueId)}/${poId}`);
    return response.data;
  },

  createOrder: async (
    venueId: string,
    data: {
      supplierId: string;
      expectedDeliveryDate?: string;
      notes?: string;
    },
  ): Promise<PurchaseOrder> => {
    // venue-scoped mount derives venueId from the URL; body venueId is ignored.
    const response = await api.post(buildBase(venueId), data);
    return response.data;
  },

  updateOrder: async (
    venueId: string,
    poId: string,
    data: { expectedDeliveryDate?: string | null; notes?: string },
  ): Promise<PurchaseOrder> => {
    const response = await api.put(`${buildBase(venueId)}/${poId}`, data);
    return response.data;
  },

  getLines: async (
    venueId: string | undefined,
    poId: string,
  ): Promise<PaginatedResponse<PurchaseOrderLine>> => {
    const response = await api.get(`${buildBase(venueId)}/${poId}/line`, { params: { pageSize: 500 } });
    return response.data;
  },

  /** Fix 5: aggregated received qty per PO line across all posted receipts. */
  getLineTotals: async (
    venueId: string | undefined,
    poId: string,
  ): Promise<Array<{ purchaseOrderLineId: string; totalReceivedQty: number }>> => {
    const response = await api.get(`${buildBase(venueId)}/${poId}/line-totals`);
    return response.data;
  },

  createLine: async (
    venueId: string,
    poId: string,
    data: {
      inventoryItemId: string;
      orderedQty: number;
      unitPrice?: number;
    },
  ): Promise<PurchaseOrderLine> => {
    const response = await api.post(`${buildBase(venueId)}/${poId}/line`, data);
    return response.data;
  },

  updateLine: async (
    venueId: string,
    poId: string,
    lineId: string,
    data: { orderedQty?: number; unitPrice?: number },
  ): Promise<PurchaseOrderLine> => {
    const response = await api.put(`${buildBase(venueId)}/${poId}/line/${lineId}`, data);
    return response.data;
  },

  deleteLine: async (venueId: string, poId: string, lineId: string): Promise<void> => {
    await api.delete(`${buildBase(venueId)}/${poId}/line/${lineId}`);
  },

  markOrdered: async (venueId: string, poId: string): Promise<PurchaseOrder> => {
    const response = await api.post(`${buildBase(venueId)}/${poId}/mark-ordered`);
    return response.data;
  },

  cancel: async (venueId: string, poId: string): Promise<PurchaseOrder> => {
    const response = await api.post(`${buildBase(venueId)}/${poId}/cancel`);
    return response.data;
  },

  close: async (venueId: string, poId: string): Promise<PurchaseOrder> => {
    const response = await api.post(`${buildBase(venueId)}/${poId}/close`);
    return response.data;
  },

  getReceipts: async (
    venueId: string | undefined,
    poId: string,
  ): Promise<PaginatedResponse<GoodsReceipt>> => {
    const response = await api.get(buildGrBase(venueId), {
      params: { purchaseOrderId: poId, pageSize: 100 },
    });
    return response.data;
  },
};

export default purchaseOrderService;
